import { fetch as router } from "helpers/router";
import fetch from "isomorphic-fetch";
import request from "utils/request";
import { captcha, csrf } from "utils/requestHeaders";
import { generateRequestData } from "widgets/Donation/store/paymentData";
import { donationFormHasAddress } from "widgets/Donation/utils/fieldHelpers";
import * as Sentry from "@sentry/react";

// Initial State ----------------------

const initialState = {
  isLoading: true,
  paymentIntentsId: null,
  paymentIntentError: null,
  cardField: {
    error: "Please enter your card details",
    invalid: true,
    touched: false,
    value: undefined,
  },
  errorMessage: null,
  isError: false,
  isSubmitting: false,
  optionalTier: 2,
  directDebitSession: "",
  requiresManualCaptcha: null,
  recaptchaErrorType: null,
  declines: 0,
  isBlocked: false,
  isIntentRefreshing: false,
  shouldRefreshCustomer: false,
};

// Actions ----------------------

export const SUBMIT = "donation/SUBMIT";
const submit = () => ({
  type: SUBMIT,
});

export const SUBMIT_FAILURE = "donation/SUBMIT_FAILURE";
export const submitFailure = (error) => ({
  type: SUBMIT_FAILURE,
  error,
});

export const OPTIONAL_TIER = "donation/OPTIONAL_TIER";
const optionalTier = (tier) => ({
  type: OPTIONAL_TIER,
  data: tier,
});

export const UPDATE_CARD = "donation/UPDATE_CARD";
const updateCard = (field) => ({
  type: UPDATE_CARD,
  data: field,
});

export const DIRECT_DEBIT_SESSION_FAILED = "donation/DIRECT_DEBIT_SESSION_FAILED";
const directDebitSessionFailed = (status) => ({
  type: DIRECT_DEBIT_SESSION_FAILED,
  data: status,
});

export const DIRECT_DEBIT_SESSION_COMPLETED = "donation/DIRECT_DEBIT_SESSION_COMPLETED";
const directDebitSessionCompleted = (status) => ({
  type: DIRECT_DEBIT_SESSION_COMPLETED,
  data: status,
});

export const CREATE_PAYMENT_INTENT_INITIATED = "donation/CREATE_PAYMENT_INTENT_INITIATED";
const createPaymentIntentInitiated = () => ({
  type: CREATE_PAYMENT_INTENT_INITIATED,
});

export const CREATE_PAYMENT_INTENT_FAILURE = "donation/CREATE_PAYMENT_INTENT_FAILURE";
const createPaymentIntentFailure = (error) => ({
  type: CREATE_PAYMENT_INTENT_FAILURE,
  error,
});

export const CREATE_PAYMENT_INTENT_SUCCESS = "donation/CREATE_PAYMENT_INTENT_SUCCESS";
const createPaymentIntentSuccess = (data) => ({
  type: CREATE_PAYMENT_INTENT_SUCCESS,
  data,
});

export const PAYMENTS_BLOCKED = "donation/PAYMENTS_BLOCKED";
const submitPaymentsBlocked = () => ({
  type: PAYMENTS_BLOCKED,
});

export const REFRESH_PAYMENT_INTENT_INITIATED = "donation/REFRESH_PAYMENT_INTENT_INITIATED";
const refreshPaymentIntentInitiated = () => ({
  type: REFRESH_PAYMENT_INTENT_INITIATED,
});

export const REFRESH_PAYMENT_INTENT_FAILURE = "donation/REFRESH_PAYMENT_INTENT_FAILURE";
const refreshPaymentIntentFailure = (error) => ({
  type: REFRESH_PAYMENT_INTENT_FAILURE,
  error,
});

export const REFRESH_PAYMENT_INTENT_SUCCESS = "donation/REFRESH_PAYMENT_INTENT_SUCCESS";
const refreshPaymentIntentSuccess = (data) => ({
  type: REFRESH_PAYMENT_INTENT_SUCCESS,
  data,
});

export const RESET_REFRESH_CUSTOMER = "donation/RESET_REFRESH_CUSTOMER";
const resetRefreshCustomer = () => ({
  type: RESET_REFRESH_CUSTOMER,
});

export const createPaymentIntent = (
  vendor,
  campaign,
  currency,
  captchaToken,
  paymentMethodTypes,
) => (dispatch) => {
  dispatch(createPaymentIntentInitiated());
  const url = router("api.v2.payments.payment-intent.store", { vendor, type: "connect" });

  const body = {
    campaign,
    currency,
    amount: 1000,
    paymentMethodTypes,
  };

  const req = request(
    url,
    "POST",
    {
      ...csrf(document),
      ...captcha(captchaToken, "v3"),
    },
    body,
  );

  return new Promise((resolve, reject) => {
    fetch(req)
      .then((response) => {
        if (response.status === 401) {
          if (response.headers.has("x-block-reason")) {
            dispatch(submitPaymentsBlocked());
          }
          throw Error();
        }
        return response.json();
      })
      .then((json) => {
        dispatch(createPaymentIntentSuccess(json.data?.code));
      })
      .catch((error) => {
        dispatch(createPaymentIntentFailure(error));
        dispatch(createPaymentIntentFailure("critical"));
        reject(error);
      });
  });
};

export const refreshPaymentIntent = (
  paymentIntentId,
  campaignId,
) => (dispatch) => {
  dispatch(refreshPaymentIntentInitiated());
  const url = router("web.pay.payment.stripe.intent.refresh", { campaign: campaignId });

  const body = {
    payment_intent: paymentIntentId,
  };

  const req = request(url, "PATCH", csrf(document), body);

  return new Promise((resolve, reject) => {
    fetch(req)
      .then((response) => {
        if (response.status === 401) {
          if (response.headers.has("x-block-reason")) {
            dispatch(submitPaymentsBlocked());
          }
          throw Error();
        }
        return response.json();
      })
      .then((json) => {
        dispatch(refreshPaymentIntentSuccess(json.paymentIntentId));
      })
      .catch((error) => {
        dispatch(refreshPaymentIntentFailure(
          "There was an issue communicating with our payment provider."
            + "Please refresh the page and try again.",
        ));
        reject(error);
      });
  });
};

export const submitPaymentElement = (data) => (dispatch) => {
  dispatch(submit());
  const formBody = generateRequestData(data, "update_payment_intent");
  const url = router("web.pay.payment.stripe.intent.update", { campaign: data.recipientId });
  const req = request(url, "PATCH", csrf(document), formBody);

  return new Promise((resolve, reject) => {
    fetch(req)
      .then((response) => response.json())
      .then((json) => {
        if (json.success === true) {
          setTimeout(() => {
            data
              .submitPaymentElementPaymentIntent({
                cardErrorHandler: data.cardErrorHandler,
                dispatch,
                elements: data.elements,
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                paymentIntentsId: data.paymentIntentsId,
                returnUrl: json.data.redirectUrl,
                stripe: data.stripe,
                address: {
                  line1: data.addressLine1 ?? "",
                  // line2 must be provided in the data, but we never collect it via Chuffed, so this is always blank
                  line2: "",
                  city: data.addressLocality ?? "",
                  state: data.addressRegion ?? "",
                  postal_code: data.addressPostcode ?? "",
                  country: getPaymentIntentCountry(json),
                },
                donationFormHasAddress: donationFormHasAddress(data.donationFormFields),
                updateBlockPayments: data.updateBlockPayments,
              });
          }, 1000);
        } else {
          if (json.reason === "BlockedPayments") {
            dispatch(submitPaymentsBlocked());
          }

          const message = json.message && json.message.trim() !== ""
            ? json.message
            : "There was an error submitting your payment.";

          const error = new Error(message);

          Sentry.captureException(error, {
            contexts: {
              updateData: json,
            },
          });

          throw error;
        }
      })
      .catch((error) => {
        const message = error.message && error.message.trim() !== ""
          ? error.message
          : "There was an error submitting your payment.";

        dispatch(submitFailure(message));
        reject(message);
      });
  });
};

const submitStripePayment = (data, type, dispatch) => {
  let url = router("web.pay.payment.stripe.direct-debit.store", { campaign: data.recipientId });
  // for generic stripe payment
  if (type === "update_payment_intent") {
    dispatch(submit());

    url = router("web.pay.payment.stripe.intent.update", { campaign: data.recipientId });
  }

  const formBody = generateRequestData(data, type);

  const method = type === "update_payment_intent" ? "PATCH" : "POST";
  const req = request(url, method, csrf(document), formBody);

  if (type === "direct_debit") {
    return new Promise((resolve, reject) => {
      fetch(req)
        .then((response) => {
          if (response.status !== 200) {
            throw Error(response.statusText);
          }
          return response;
        })
        .then((response) => response.json())
        .then((json) => {
          if (json.success === "success") {
            dispatch(directDebitSessionCompleted());
            resolve(json);
          } else {
            dispatch(directDebitSessionFailed(json.message));
          }
        })
        .catch((error) => {
          dispatch(directDebitSessionFailed(error.message));
          reject(error);
        });
    });
  }

  return new Promise((resolve, reject) => {
    fetch(req)
      .then((response) => response.json())
      .then((json) => {
        if (json.success === true) {
          const stripeMethods = data?.stripe;
          const cardElement = data?.cardElem;
          data
            .submitPaymentIntent(
              data,
              json.data.redirectUrl,
              data.closeFeeConfirmation,
              data.paymentMethod,
              data.paymentIntentComplete,
              stripeMethods,
              cardElement,
              data.cardErrorHandler,
            );
        } else {
          dispatch(submitFailure(json.message));
        }
      })
      .catch((error) => {
        dispatch(submitFailure(error.message));
        reject(error.message);
      });
  });
};

export const submitDonation = (data) => (dispatch) => {
  dispatch(submit());
  // if no payment method, means shared/flat fee which requires no pre-selection of payment method
  const paymentProvider = data.paymentMethod || data.submitButton;

  if (paymentProvider !== "paypal") {
    // Unsupported payment method, do nothing
    return;
  }

  const url = router("web.pay.payment.paypal-marketplace", { campaign: data.recipientId });

  const formData = generateRequestData(data, "paypal");

  const req = request(url, "POST", csrf(document), formData);

  return new Promise((resolve, reject) => {
    fetch(req)
      .then((response) => response.json())
      .then((json) => {
        if (json.success === true) {
          window.location = json.redirectUrl;
          resolve(json);
        } else {
          dispatch(submitFailure(json.message));
        }
      })
      .catch((error) => {
        dispatch(submitFailure(error));
        reject(error);
      });
  });
};

export const updatePaymentIntent = (data) => (dispatch) => {
  submitStripePayment(data, "update_payment_intent", dispatch);
};

export const submitDirectDebit = (data) => (dispatch) => {
  submitStripePayment(data, "direct_debit", dispatch);
};

export const setOptionalTier = (tier) => (dispatch) => {
  dispatch(optionalTier(tier));
};

export const setCardField = (field) => (dispatch) => {
  dispatch(updateCard(field));
};

export const resetRefreshStripeCustomer = () => (dispatch) => {
  dispatch(resetRefreshCustomer());
};

export const INITIALIZE_DONATION_FORM = "donation/INITIALIZE_DONATION_FORM";
export const initializeDonationForm = () => ({
  type: INITIALIZE_DONATION_FORM,
});

const getPaymentIntentCountry = (updateData) =>
  updateData.data.donationCountry?.country_iso_code?.toUpperCase() ?? null;

// Reducers ----------------------
export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case INITIALIZE_DONATION_FORM:
      return {
        ...state,
        isLoading: false,
      };
    case CREATE_PAYMENT_INTENT_INITIATED:
      return {
        ...state,
        paymentIntentsId: null,
      };
    case CREATE_PAYMENT_INTENT_SUCCESS:
      return {
        ...state,
        isLoading: false,
        paymentIntentsId: action.data,
        requiresManualCaptcha: false,
      };
    case CREATE_PAYMENT_INTENT_FAILURE:
      return {
        ...state,
        isLoading: false,
        paymentIntentError: action.error,
        requiresManualCaptcha: true,
        recaptchaErrorType: action.error,
      };
    case SUBMIT:
      return {
        ...state,
        errorMessage: "",
        isError: false,
        isSubmitting: true,
      };
    case SUBMIT_FAILURE:
      return {
        ...state,
        isLoading: false,
        errorMessage: action.error || "Unable to make payment. Please try again.",
        isError: true,
        isSubmitting: false,
        shouldRefreshCustomer: true,
      };
    case OPTIONAL_TIER:
      return {
        ...state,
        isLoading: false,
        optionalTier: action.data,
      };
    case UPDATE_CARD:
      return {
        ...state,
        cardField: {
          ...state.cardField,
          ...action.data,
        },
      };
    case DIRECT_DEBIT_SESSION_FAILED:
      return {
        ...state,
        directDebitSession: "failed",
        errorMessage: "",
        isLoading: false,
        isError: false,
        isSubmitting: true,
      };
    case DIRECT_DEBIT_SESSION_COMPLETED:
      return {
        ...state,
        directDebitSession: "completed",
        isLoading: false,
        errorMessage: "",
        isError: false,
        isSubmitting: true,
      };
    case PAYMENTS_BLOCKED:
      return {
        ...state,
        isBlocked: true,
      };
    case REFRESH_PAYMENT_INTENT_INITIATED:
      return {
        ...state,
        isIntentRefreshing: true,
      };
    case REFRESH_PAYMENT_INTENT_SUCCESS:
      return {
        ...state,
        isIntentRefreshing: false,
        paymentIntentsId: action.data,
      };
    case REFRESH_PAYMENT_INTENT_FAILURE:
      return {
        ...state,
        isIntentRefreshing: false,
        paymentIntentRefreshError: action.error,
      };
    case RESET_REFRESH_CUSTOMER:
      return {
        ...state,
        shouldRefreshCustomer: false,
      };
    default:
      return state;
  }
}
